<template>
  <v-container class="AssignmentDetailsPage pb-7">
    <SchoolTitleBar
      v-if="assignmentTitle || loading.assignmentData"
      :title="loading.assignmentData ? 'Loading...' : assignmentTitle"
    />

    <div
      v-if="loading.assignmentData"
      class="d-flex justify-center pa-5"
    >
      <v-progress-circular
        indeterminate
        size="60"
      />
    </div>
    <template v-else-if="assignment">
      <ChipsColumns :items="infoChipItems" class="mt-5 mb-10" />

      <AssignmentStartTestCard
        v-if="currentPerformStatus === PerformAssignmentStatus.Preparing"
        :assignment="assignment"
        :loading="testLoading"
        :attempts-left="attemptsLeft"
        :can-run-test="canRunTest"
        @start="startTest"
      />
      <AssignmentRunningTestCard
        v-if="currentPerformStatus === PerformAssignmentStatus.Started"
        :assignment-test="assignmentTest"
        :answers.sync="testAnswers"
        :loading="testLoading"
        @update:answers="updateTestDebounced"
        @finish="finishTest"
      />
      <AssignmentFinishedTestCard
        v-if="currentPerformStatus === PerformAssignmentStatus.Finished"
        :assignment="assignment"
        :loading="testLoading"
        :attempts-left="attemptsLeft"
        :can-run-test="canRunTest"
        @start="startTest"
        @open-report="openDetailedReport"
      />
    </template>
    <div v-else class="d-flex justify-center pa-5">
      <h5 class="text-h5 red--text">Assignment not found (404)</h5>
    </div>

    <AssignmentReportDialog
      v-model="reportDialog.active"
      :assignment-id="reportDialog.assignmentId"
      :report-id="reportDialog.reportId"
    />
  </v-container>
</template>

<script>
import { debounce } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { handleReqErrorWithToast } from '@/helpers/error-handlers';

const PerformAssignmentStatus = {
  Preparing: 'Preparing',
  Started: 'Started',
  Finished: 'Finished',
};

let tickTimersInterval = null;

export default {
  name: 'AssignmentTestPage',
  components: {
    SchoolTitleBar: () => import('@/components/profiles/SchoolTitleBar'),
    ChipsColumns: () => import('@/components/profiles/ChipsColumns'),
    AssignmentStartTestCard: () => import('@/components/assignments/AssignmentStartTestCard'),
    AssignmentRunningTestCard: () => import('@/components/assignments/AssignmentRunningTestCard'),
    AssignmentFinishedTestCard: () => import('@/components/assignments/AssignmentFinishedTestCard'),
    AssignmentReportDialog: () => import('@/components/modals/assignments/AssignmentReportDialog'),
  },
  data() {
    return {
      PerformAssignmentStatus,
      loading: {
        assignmentData: false,
      },
      loadedAssignment: null,
      assignmentTest: null,
      testAnswers: {},
      updateTestDebounced: null,
      finishTestError: null,
      reportDialog: {
        active: false,
        assignmentId: null,
        reportId: null,
      },
    };
  },
  computed: {
    ...mapGetters('assignments', [
      'assignments',
      'assignmentsTimers',
      'currentPeriodAssignments',
      'overdueTimerAssignments',
      'testsLoading',
      'testsUpdatesLoading',
    ]),
    routeAssignmentId() {
      return this.$route.params.assignmentId;
    },
    assignment() {
      return this.assignments.find(a => a._id === this.routeAssignmentId) || this.loadedAssignment;
    },
    assignmentId() {
      return this.assignment?._id;
    },
    assignmentTitle() {
      return this.assignment ? `ASSIGNMENT: ${this.assignment.name}` : null;
    },
    testLoading() {
      return this.testsLoading[this.assignment?._id];
    },
    testUpdateLoading() {
      return this.testsUpdatesLoading[this.assignment?._id];
    },
    canRunTest() {
      const isTestPeriod = this.currentPeriodAssignments.some(a => a._id === this.assignmentId);
      const isAttemptsLeft = this.attemptsLeft > 0;
      const isRunningAssignment = !!this.assignmentsTimers[this.assignmentId];
      return isTestPeriod && (isAttemptsLeft || isRunningAssignment);
    },
    attemptsLeft() {
      const { assignment } = this;
      return assignment?.contentSettings.maxAttempts - assignment?.reports?.length;
    },
    timerValue() {
      const timerDuration = this.assignmentsTimers[this.assignmentId];
      const assignmentDuration = this.$moment.utc().startOf('day').add(this.assignment.duration, 'minutes');
      const timerMoment = timerDuration || assignmentDuration;
      return timerMoment.format('HH:mm:ss');
    },
    isOverdueTimer() {
      return this.overdueTimerAssignments.some(a => a._id === this.assignmentId);
    },
    infoChipItems() {
      const { assignment } = this;

      const chips = [
        {
          icon: 'notes',
          title: 'Name',
          text: assignment?.name,
        },
        {
          icon: 'pending_actions',
          title: 'Due',
          text: this.$moment(assignment?.dueDate).format('MM/DD/YYYY hh:mm A'),
        },
        {
          icon: 'replay_circle_filled',
          title: 'Attempts',
          text: `${this.attemptsLeft}/${assignment?.contentSettings.maxAttempts}`,
        },
      ];

      if (assignment?.duration) {
        const timerChip = {
          icon: 'timer',
          title: 'Timer',
          text: this.timerValue,
        };
        chips.splice(1, 0, timerChip);
      }

      return chips;
    },
    currentPerformStatus() {
      const reports = this.assignment?.reports || [];
      const lastReport = reports[reports.length - 1];

      if (lastReport && lastReport.finished) {
        return PerformAssignmentStatus.Finished;
      }
      if (this.assignmentTest) {
        return PerformAssignmentStatus.Started;
      }
      return PerformAssignmentStatus.Preparing;
    },
  },
  created() {
    this.loadAssignmentInfo();
    this.updateTestDebounced = debounce(this.updateTest, 4000, {
      leading: true,
      maxWait: 7000,
    });
  },
  mounted() {
    tickTimersInterval = setInterval(this.onTestTimerIntervalTick, 1000);
  },
  beforeDestroy() {
    clearInterval(tickTimersInterval);
  },
  methods: {
    ...mapActions('assignments', [
      'loadAssignment',
      'startAssignmentTest',
      'updateAssignmentTest',
      'finishAssignmentTest',
      'tickTestsTimer',
    ]),
    onTestTimerIntervalTick() {
      this.tickTestsTimer();

      if (this.isOverdueTimer && !this.testLoading && !this.finishTestError) {
        this.finishTest();
      }
    },
    loadAssignmentInfo() {
      this.loading.assignmentData = true;
      this.loadAssignment({ assignmentId: this.routeAssignmentId })
        .then(assignment => (this.loadedAssignment = assignment))
        .catch(err => handleReqErrorWithToast(err, 'Failed to load assignment info'))
        .finally(() => (this.loading.assignmentData = false));
    },
    startTest() {
      this.startAssignmentTest({ assignmentId: this.assignmentId })
        .then((data) => {
          this.assignmentTest = data.test;
          this.applyTestAnswers(data.report);
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to start the assignment test'));
    },
    updateTest() {
      this.updateAssignmentTest({
        assignmentId: this.assignmentId,
        answers: this.testAnswers,
      })
        .catch(err => handleReqErrorWithToast(err, 'Failed to save the assignment test answers'));
    },
    finishTest() {
      this.updateTestDebounced.cancel();
      this.finishTestError = null;

      this.finishAssignmentTest({
        assignmentId: this.assignmentId,
        answers: this.testAnswers,
      })
        .then(() => {
          this.clearTestData();
          this.clearTestAnswers();
          this.$toast.info('Test finished');
        })
        .catch((err) => {
          this.finishTestError = err;
          handleReqErrorWithToast(err, 'Failed to finish the assignment test');
        });
    },
    clearTestData() {
      this.assignmentTest = null;
    },
    applyTestAnswers(report) {
      report.answers.forEach((answerData) => {
        this.$set(this.testAnswers, answerData.taskId, answerData.values);
      });
    },
    clearTestAnswers() {
      const answerKeys = Object.keys(this.testAnswers);
      for (const key of answerKeys) {
        this.$delete(this.testAnswers, key);
      }
    },
    openDetailedReport(reportId) {
      Object.assign(this.reportDialog, {
        active: true,
        assignmentId: this.assignmentId,
        reportId,
      });
    },
  },
};
</script>
